import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2Icon } from 'lucide-react'
import { memo, type ReactNode, useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { DialogFooter } from '@/components/ui/dialog'
import { Form } from '@/components/ui/form'

import { InviteMemberEmailField } from './invite-member-email-field.atom'
import { InviteMemberRoleField } from './invite-member-role-field.atom'
import type { InviteMemberFormData } from './members.interface'
import { type InviteMemberFormSchemaType, inviteMemberFormSchema } from './members.schema'

interface InviteMemberFormProps {
  onSubmit: (data: InviteMemberFormData) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
  shouldReset?: boolean
}

const InviteMemberFormBase: React.FC<InviteMemberFormProps> = ({
  onSubmit,
  onCancel,
  isLoading = false,
  shouldReset = false,
}): ReactNode => {
  const form = useForm<InviteMemberFormSchemaType>({
    resolver: zodResolver(inviteMemberFormSchema),
    defaultValues: { email: '', memberRole: 'org:member' },
  })

  useEffect(() => {
    if (shouldReset) form.reset()
  }, [shouldReset, form])

  const handleSubmit = async (data: InviteMemberFormSchemaType): Promise<void> => {
    await onSubmit({ email: data.email, memberRole: data.memberRole })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="invite-member-form space-y-4">
        <InviteMemberEmailField control={form.control} isDisabled={isLoading} />
        <InviteMemberRoleField
          control={form.control}
          setValue={form.setValue}
          isDisabled={isLoading}
        />
        <DialogFooter className="invite-member-dialog-footer">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading} className="invite-member-submit-button">
            {isLoading ? (
              <>
                <Loader2Icon className="mr-2 size-4 animate-spin" />
                Sending...
              </>
            ) : (
              'Send Invitation'
            )}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  )
}

export const InviteMemberForm = memo(InviteMemberFormBase)

InviteMemberForm.displayName = 'InviteMemberForm'
